import React, { useState, useEffect } from "react";
import {
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Label,
  Button,
} from "reactstrap";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import PaymentTypeServices from "../../../services/OrderingServices/PaymentTypeService";
import { isAction } from "@reduxjs/toolkit";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Payment type name is required"),
  description: Yup.string().required("Description is required"),
  accountNumber: Yup.number()
    .min(0, "Account number must be greater than or equal to 0")
    .required("Account number is required"),
});

const EditModal = ({
  modal,
  toggleModal,
  editingPackage,
  savePackage,
  fetchPaymentType,
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setSubmitting,
    resetForm,
    setFieldValue
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: editingPackage?.id || null,
      name: editingPackage?.name || "",
      description: editingPackage?.description || "",
      accountNumber: editingPackage?.accountNumber || "",
      isActive: editingPackage?.isActive || true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("Name", values.name);
        formData.append("Description", values.description || "");
        formData.append("AccountNumber", values.accountNumber || "");
        formData.append("IsActive", values.isActive);
        if (selectedImage) {
          formData.append("Image", selectedImage);
        }

        if (values?.id) {
          await PaymentTypeServices.update(values.id, formData);
          savePackage({ ...values });
          toast.success("Payment type updated successfully");
        } else {
          await PaymentTypeServices.create(formData);
          toast.success("Payment type created successfully");
          resetForm();
          setSelectedImage(null);
        }

        fetchPaymentType();
        toggleModal();
      } catch (error) {
        console.error("Error during package update:", error);
        toast.error(
          "Error updating package: " + (error.message || "Unknown error"),
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setSelectedImage(file);
  };

  return (
    <Modal isOpen={modal} toggle={toggleModal} backdrop="static">
      <ModalHeader toggle={toggleModal}>
        {editingPackage?.id ? "Update" : "Create"} Payment Type
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            {/* Image Upload */}
            <Col lg={12} className="mb-3">
              <div className="mb-3">
                <label className="form-label" htmlFor="image">
                  Upload Image
                </label>
                <Input
                  type="file"
                  id="image"
                  name="image"
                  onChange={handleFileChange}
                  accept="image/*"
                />
                {selectedImage && (
                  <div className="mt-2">
                    <img
                      src={URL.createObjectURL(selectedImage)}
                      alt="preview"
                      width="100"
                    />
                  </div>
                )}
              </div>
            </Col>
            {/* Name */}
            <Col lg={12} className="mb-3">
              <div className="form-floating">
                <Input
                  type="text"
                  name="name"
                  id="name"
                  placeholder="Title"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-control ${
                    touched.name && errors.name ? "is-invalid" : ""
                  }`}
                />
                <Label for="name">Payment type name</Label>
                {touched.name && errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>
            </Col>

            {/* Account Number */}
            <Col lg={12} className="mb-3">
              <div className="form-floating">
                <Input
                  type="text"
                  name="accountNumber"
                  id="accountNumber"
                  placeholder="Account Number"
                  value={values.accountNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-control ${
                    touched.accountNumber && errors.accountNumber
                      ? "is-invalid"
                      : ""
                  }`}
                />
                <Label for="accountNumber">Account Number</Label>
                {touched.accountNumber && errors.accountNumber && (
                  <div className="invalid-feedback">{errors.accountNumber}</div>
                )}
              </div>
            </Col>

            {/* Description */}
            <Col lg={12} className="mb-3">
              <div className="mb-3">
                <label className="form-label" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  className={`form-control ${
                    touched.description && errors.description
                      ? "is-invalid"
                      : ""
                  }`}
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.description && errors.description && (
                  <div className="invalid-feedback">{errors.description}</div>
                )}
              </div>
            </Col>

            <Col lg={12} className="mb-3">
              <div className="form-check form-switch">
                <Input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={values.isActive}
                  onChange={(e) => {
                    setFieldValue("isActive", e.target.checked);
                  }}
                  className="form-check-input"
                />
                <Label className="form-check-label" for="isActive">
                  {values.isActive ? "Active" : "Inactive"}
                </Label>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={toggleModal}>
            Cancel
          </Button>
          <Button type="submit" color="success" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditModal;
